import { getRegionByCode } from 'modules/checkout/utils/regions'
import { CheckoutCartResponse } from '_app/graphql/types'
import {
  CustomerAddress,
  Maybe,
  Customer,
  Country,
  CartAddressInput,
} from '_app/types/api'

type InpostShowroomAddresss = Record<string, string | string[]>;
type Options = {
  data: CheckoutCartResponse | undefined;
  isFakeAddress: boolean;
  customer: Customer | undefined;
  handleInpostShowroomAddress: () => InpostShowroomAddresss;
  areTermsAccepted: boolean;
  countries: Country[] | undefined;
  isInpostOrShowroom: boolean;
};

type FakeAddressProps = Pick<Options, 'customer' | 'data'> & {
  inpostShowroomAddressData: InpostShowroomAddresss;
  defaultAddress: Maybe<CartAddressInput> | undefined;
};
const getFakeAddress = ({
  customer,
  defaultAddress,
  data,
  inpostShowroomAddressData,
}: FakeAddressProps) => {
  const countryCode =
    data?.cart?.shipping_addresses?.[0]?.country?.code || 'PL'

  return {
    email: customer?.email || undefined,
    accept_regulations: false,
    subscribe_to_newsletter_email: false,
    receive_invoice: false,
    send_receipt_to_different_address: false,
    shipping_address: {
      __typename: 'ShippingAddress' as const,
      firstname: customer?.firstname || undefined,
      lastname: customer?.lastname || undefined,
      company: undefined,
      vat_id: undefined,
      postcode: undefined,
      city: undefined,
      street: undefined,
      country_code: countryCode,
      telephone: undefined,
      ...defaultAddress,
      ...inpostShowroomAddressData,
    },
    billing_address: {
      invoice_type: 'company' as const,
      country_code: countryCode,
    },
  }
}

const parseToAddressInput = (
  address: Maybe<CustomerAddress> | undefined
): CartAddressInput | undefined => {
  if (!address) {
    return undefined
  }

  return {
    ...address,
    city: address.city || '',
    country_code: address.country_code || '',
    firstname: address.firstname || '',
    lastname: address.lastname || '',
    street: address.street || [],
    telephone: address.telephone || '',
    region: address.region?.region_code || null,
    region_id: address.region?.region_id || null,
  }
}

const getDefaultAddresses = (customer: Customer | undefined) => {
  const defaultShippingAddress = (customer?.addresses || []).find(
    a => a?.default_shipping
  )

  const defaultBillingAddress = (customer?.addresses || []).find(
    a => a?.default_billing
  )

  return [
    parseToAddressInput(defaultShippingAddress),
    parseToAddressInput(defaultBillingAddress),
  ]
}

const getBillingAddressProperties = (
  billingAddress: Maybe<CartAddressInput> | undefined,
  shippingAddress: Maybe<CartAddressInput> | undefined,
  isInpostOrShowroom: boolean
) => {
  if (billingAddress?.firstname) {
    return billingAddress
  }

  if (isInpostOrShowroom) {
    return
  }

  return shippingAddress
}

const validatedDefaultShippingAddress = (
  address: CartAddressInput | undefined,
  countryCode: string
) => {
  const isSameCountry = countryCode === address?.country_code

  return isSameCountry ? address : undefined
}

export const getDefaultValues = ({
  data,
  isFakeAddress,
  customer,
  handleInpostShowroomAddress,
  areTermsAccepted,
  countries,
  isInpostOrShowroom,
}: Options): any => {
  const inpostShowroomAddressData = handleInpostShowroomAddress()
  const [defaultShippingAddressRaw, defaultBillingAddress] =
    getDefaultAddresses(customer)
  const countryCode = data?.cart?.shipping_addresses?.[0]?.country?.code || ''
  const defaultShippingAddress = validatedDefaultShippingAddress(
    defaultShippingAddressRaw,
    countryCode
  )

  if (isFakeAddress) {
    return getFakeAddress({
      customer,
      data,
      defaultAddress: defaultShippingAddress,
      inpostShowroomAddressData,
    })
  }

  const region = getRegionByCode(
    data?.cart?.shipping_addresses?.[0]?.region?.code,
    countries?.find(country => country.id === countryCode)
  )

  const shippingAddress = data?.cart?.shipping_addresses[0]
    ? parseToAddressInput({
        ...data.cart.shipping_addresses[0],
        country_code: countryCode,
        region: {
          region_code: region?.code,
          region_id: region?.id,
          region: region?.name,
        },
        ...inpostShowroomAddressData,
      } as CustomerAddress)
    : defaultShippingAddress

  const billingAddress = {
    invoice_type: 'company',
    ...getBillingAddressProperties(
      defaultBillingAddress,
      shippingAddress,
      isInpostOrShowroom
    ),
  }

  return {
    shipping_address: shippingAddress,
    billing_address: billingAddress,
    email: data?.cart?.email || '',
    receive_invoice: false,
    accept_regulations: !!customer || areTermsAccepted,
    subscribe_to_newsletter_email: false,
    send_receipt_to_different_address: false,
  }
}
